import { EnvironmentFilled, PlusOutlined } from '@ant-design/icons';
import { Button, Collapse, Modal } from 'antd';
import clsx from 'clsx';
import { useContext } from 'react';
import { isEmpty } from 'underscore';
import Api from '../../../api/Api';
import Analysis from '../../../assets/icon/Analysis.svg?react';
import Report from '../../../assets/icon/Report.svg?react';
import Validation from '../../../assets/icon/Validation.svg?react';
import LoadingContext from '../../../context/loadingContext/LoadingContext';
import { useLocationAvailable } from '../../../hooks/useLocationAvailable';
import useGetShoppingCartQuery from '../../../queries/useGetShoppingCartQuery';
import { useOverlayData } from '../../../queries/useOverlayQuery';
import useAuthStore from '../../../store/authStore';
import useDatasetStore from '../../../store/datasetStore';
import useHomeStore from '../../../store/homeStore';
import { MarkerItem } from '../../../types/home';
import { pointIsInCart } from '../../../utils/PointHelper';
import { ITEM, TabKey } from '../../../utils/common';
import { isDatasetOfflineFromBriefs } from '../../../utils/offlineDatasetGuard';
import { IWmsProps } from '../Map/Layer/DatasetMesh';
import styles from './Style/ResultPanel.module.scss';
import { AnalyticsContainer } from './component/AnalyticsContainer';
import CategoryAccordion from './component/CategoryAccordion';
import {
  DatasetHasNoAnalyticsModal,
  DatasetHasNoReportModal,
  DatasetOfflineModal,
  ReportsModal,
} from './component/DatasetModal';
import { PointActions } from './component/PointActions';
import { useCartManagement } from './hooks/useCartManagement';
import { useDatasetAvailability } from './hooks/useDatasetAvailability';
import { useMarkerManagement } from './hooks/useMarkerManagement';
import { usePopupManagement } from './hooks/usePopupManagement';

const Panel = Collapse.Panel;

export interface ResultPanelProps {
  isAddMarkerMode: boolean;
  open: boolean;
  onMeshControl: (value: string) => void;
  addCoords: (coords: number[]) => void;
  onMark: (bool: boolean) => void;
  coordsMoved: boolean;
  onResetCoordsMove: () => void;
  setMeshUrl: (item?: IWmsProps) => void;
}

export default function ResultPanel(props: ResultPanelProps) {
  const { data } = useGetShoppingCartQuery();

  const shoppingItems = data?.shoppingItems || {};

  const user = useAuthStore((state) => state.user);
  const userGroupPermissions = useAuthStore((state) => state.userGroupPermissions);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const setShowLogin = useHomeStore((state) => state.setShowLogin);

  const datasetBriefs = useDatasetStore((state) => state.datasetBriefs);

  const loadingContext = useContext(LoadingContext);

  const {
    activeMarker,
    nextFreePointNumber,
    activePanelKey,
    markers,
    isChangeCoordinatesWarningVisible,
    resetCoordsKey,
    onChangePointLocation,
    onChangeActiveKey,
    onPointsDelete,
    onPointNameChange,
    validateInput,
    handleChangeCoordinatesWarningOk,
    handleChangeCoordinatesWarningCancel,
    setAddMarkerMode,
  } = useMarkerManagement({ loadingContext, props });

  const {
    showPopup,
    onChangePoint,
    datasetIsOfflineWarningVisible,
    datasetOfflineText,
    datasetNoReportVisible,
    isReportsVisible,
    activePlotValidation,
    updating,
    links,
    error,
    setDatasetOfflineText,
    setDatasetIsOfflineWarningVisible,
    setDatasetNoReportVisible,
    setIsReportsVisible,
    handleVisibleNewPopup,
    switchVisiblePopup,
    handlePopupKey,
    showNewPopup,
    onUpdatePreprocessedDataLink,
    setActivePlotValidation,
    datasetHasNoAnalyticsVisible,
    setDatasetHasNoAnalyticsVisible,
  } = usePopupManagement(activeMarker, onChangeActiveKey);

  const {
    loadingCart,
    handleAllToCart,
    doAddProductToCart,
    removeAllItemsFromPoint,
    shouldShowRemoveButton,
    cartLimitModalContextHolder,
  } = useCartManagement(activeMarker, setDatasetOfflineText, setDatasetIsOfflineWarningVisible);

  useOverlayData(activeMarker?.dataset ?? '');

  const { category, isDatasetOffline, isDatasetHasReport } = useDatasetAvailability(activeMarker);

  const isDatasetOfflineFromBrief = activeMarker?.dataset
    ? isDatasetOfflineFromBriefs(activeMarker.dataset, datasetBriefs)
    : false;

  const isSelectedDatasetOffline = isDatasetOffline || isDatasetOfflineFromBrief;

  const isLocationAvailable = useLocationAvailable(
    category as string,
    activeMarker?.dataset,
    datasetBriefs,
    isSelectedDatasetOffline
  );

  const handleOpenPopup = (key: TabKey) => {
    showNewPopup(key, isSelectedDatasetOffline);
  };

  const panelForPoint = ({
    index,
    id,
    addCoords,
    onMeshControl,
    showPoint,
    markerPoint,
  }: {
    index: number;
    id: string;
    addCoords: (coords: number[]) => void;
    onMeshControl: (value: string) => void;
    showPoint: boolean;
    markerPoint?: MarkerItem;
  }) => {
    let pointIsInCard = false;

    if (!showPoint) return false;

    if (markerPoint) {
      const inCart = pointIsInCart(markerPoint, shoppingItems);
      if (inCart) {
        const itemsPerPoint = shoppingItems[markerPoint.label];
        const categories = Object.keys(itemsPerPoint);
        const firstCategory = itemsPerPoint[categories[0]];
        // Take also coordinates into account to allow editing of points with same name from previous sessions
        pointIsInCard = markerPoint.coordinates.join() === firstCategory.location.replace(' ', '');
      }
    }

    return (
      <Panel
        className={`
          ${
            props.isAddMarkerMode && markers.length > 0 && !markerPoint
              ? styles.addPanel
              : styles.panel
          } ${
          markerPoint?.active && !props.isAddMarkerMode
            ? 'markerSelected '
            : props.isAddMarkerMode && markers.length > 0 && !markerPoint
            ? 'markerSelected '
            : ''
        }`}
        header={
          <PointActions
            id={id}
            index={index}
            markerPoint={markerPoint}
            editable={!pointIsInCard}
            addCoords={addCoords}
            pointIsInCard={pointIsInCard}
            onChangePointLocation={onChangePointLocation}
            resetCoordsKey={resetCoordsKey}
            onPointNameChange={onPointNameChange}
            onPointsDelete={onPointsDelete}
            onMark={props.onMark}
            validateInput={validateInput}
            isAddMarkerMode={props.isAddMarkerMode}
          />
        }
        key={id}
      >
        {!markerPoint || isEmpty(markerPoint!.coordinates) ? null : (
          <div className={styles.resultTabs}>
            <CategoryAccordion
              onAddProductToCart={doAddProductToCart}
              list={markerPoint.downloadPage}
              coords={markerPoint.coordinates}
              markerId={markerPoint.id}
              onMeshControl={onMeshControl}
              setMeshUrl={props.setMeshUrl}
              setDatasetIsOfflineWarningVisible={setDatasetIsOfflineWarningVisible}
            />
          </div>
        )}
      </Panel>
    );
  };

  return (
    <>
      {markers.map((point, idx) => {
        const isTnw =
          point?.downloadPage[0]?.datasetId.toLowerCase().includes('tnw') && user.authenticated;
        const cell = point?.cellSize?.[category as string]?.toString() ?? '999';
        const waterDepth = point?.waterDepth?.[category as string]?.toString() ?? '999';
        const userGroupPermission =
          userGroupPermissions[point.id || '']?.[category as string] ?? null;
        return (
          <AnalyticsContainer
            category={category}
            point={point}
            key={`analytics-validation-modal-${point.id}-${idx}`}
            activePoint={point}
            isPopupVisible={showPopup?.[point.id]?.visible ?? false}
            setIsPopupVisible={handleVisibleNewPopup}
            switchVisiblePopup={switchVisiblePopup}
            setDatasetIsOfflineWarningVisible={setDatasetIsOfflineWarningVisible}
            userGroupPermissions={userGroupPermission}
            onMeshControl={props.onMeshControl}
            isTnw={isTnw}
            gridCellSize={cell}
            waterDepth={waterDepth}
            onChangePointLocation={onChangePointLocation}
            activePlotValidation={activePlotValidation}
            setActivePlotValidation={setActivePlotValidation}
            activeTab={showPopup?.[point.id]?.key ?? ITEM.VALIDATION}
            setActiveTab={handlePopupKey}
            onChangeActiveKey={onChangePoint}
            isAuthorized={isAuthenticated()}
            setShowLogin={setShowLogin}
          />
        );
      })}
      {props.isAddMarkerMode && markers?.length === 0 && (
        <div className={`${styles.addOnePoint} ${props.open ? styles.rootOpen : ''}`}>
          {panelForPoint({
            index: 0,
            id: nextFreePointNumber,
            addCoords: props.addCoords,
            onMeshControl: props.onMeshControl,
            showPoint: props.isAddMarkerMode && markers?.length === 0,
          })}
        </div>
      )}
      <div
        className={`${styles.root} ${props.open && markers?.length > 0 ? styles.rootOpen : ''} ${
          markers?.length === 0 ? styles.rootHidden : ''
        }`}
      >
        <div style={{ height: '100%' }}>
          <DatasetOfflineModal
            text={datasetOfflineText}
            datasetIsOfflineWarningVisible={datasetIsOfflineWarningVisible}
            handleCloseDatasetIsOfflineWarning={setDatasetIsOfflineWarningVisible}
          />
          <DatasetHasNoAnalyticsModal
            isVisible={datasetHasNoAnalyticsVisible}
            handleClose={setDatasetHasNoAnalyticsVisible}
          />
          <DatasetHasNoReportModal
            isVisible={datasetNoReportVisible}
            handleClose={setDatasetNoReportVisible}
          />

          <ReportsModal
            activePoint={activeMarker}
            error={error}
            category={category as string}
            isReportsVisible={isReportsVisible}
            links={links}
            onUpdatePreprocessedDataLink={onUpdatePreprocessedDataLink}
            setIsReportsVisible={setIsReportsVisible}
            updating={updating}
            waterDepth={activeMarker?.waterDepth?.[category as string]?.toString() ?? '0'}
          />

          <Modal
            open={isChangeCoordinatesWarningVisible}
            onOk={handleChangeCoordinatesWarningOk}
            onCancel={handleChangeCoordinatesWarningCancel}
            title={'Change location'}
          >
            <p>Changing the coordinates will reset your validation and analytics. Continue?</p>
          </Modal>
          {cartLimitModalContextHolder}
          <div className={styles.contentContainer}>
            <div className={styles.DatasetLabel}>
              <Button
                id="PointPanel-AddPoint_Button"
                data-testid="PointPanel-AddPoint_Button"
                className={styles.buttonBorderAddPoint}
                type="primary"
                onClick={() => setAddMarkerMode(!props.isAddMarkerMode)}
              >
                <div className={clsx(styles.buttonContent, styles.buttonContentAddPoint)}>
                  <PlusOutlined />
                  <div>Add Point</div>
                  <EnvironmentFilled />
                </div>
              </Button>
              <Button
                id="PointPanel-Validation_Button"
                data-testid="PointPanel-Validation_Button"
                className={styles.buttonBorder}
                disabled={!isLocationAvailable}
                type="default"
                onClick={() => handleOpenPopup(ITEM.VALIDATION)}
              >
                <div className={styles.buttonContent}>
                  <Validation
                    width={30}
                    height={30}
                    viewBox={'0 0 40 40'}
                    className={clsx(!isLocationAvailable ? styles.buttonIconDisabled : '')}
                  />
                  <div
                    className={clsx(
                      !isLocationAvailable ? styles.buttonLabelDisabled : styles.buttonLabel
                    )}
                  >
                    Validation
                  </div>
                </div>
              </Button>

              <Button
                id="PointPanel-Analytics_Button"
                data-testid="PointPanel-Analytics_Button"
                className={styles.buttonBorder}
                disabled={!isLocationAvailable}
                type="default"
                onClick={() => handleOpenPopup(ITEM.ANALYTICS)}
              >
                <div className={styles.buttonContent}>
                  <Analysis
                    width={30}
                    height={30}
                    viewBox={'0 0 40 40'}
                    className={clsx(!isLocationAvailable ? styles.buttonIconDisabled : '')}
                  />
                  <div
                    className={clsx(
                      !isLocationAvailable ? styles.buttonLabelDisabled : styles.buttonLabel
                    )}
                  >
                    Analytics
                  </div>
                </div>
              </Button>

              <Button
                id="PointPanel-Reports_Button"
                data-testid="PointPanel-Reports_Button"
                className={styles.buttonBorder}
                disabled={!isLocationAvailable}
                type="default"
                onClick={() => {
                  handleOpenPopup(ITEM.REPORTS);
                }}
              >
                <div className={styles.buttonContent}>
                  <Report
                    width={30}
                    height={30}
                    viewBox={'0 0 40 40'}
                    className={clsx(!isLocationAvailable ? styles.buttonIconDisabled : '')}
                  />
                  <div
                    className={clsx(
                      !isLocationAvailable ? styles.buttonLabelDisabled : styles.buttonLabel
                    )}
                  >
                    Reports
                  </div>
                </div>
              </Button>
            </div>
            <Collapse accordion activeKey={activePanelKey} onChange={onChangeActiveKey}>
              {props.isAddMarkerMode &&
                panelForPoint({
                  index: markers.length,
                  id: nextFreePointNumber,
                  addCoords: props.addCoords,
                  onMeshControl: props.onMeshControl,
                  showPoint: props.isAddMarkerMode && markers?.length > 0,
                })}
              {markers.map((markerPoint: MarkerItem, idx: number) =>
                panelForPoint({
                  index: idx,
                  id: markerPoint.id,
                  addCoords: props.addCoords,
                  onMeshControl: props.onMeshControl,
                  showPoint: markers.length > 0,
                  markerPoint,
                })
              )}
            </Collapse>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '20px',
              }}
            >
              <Button
                id="PointPanel-LocalModel_Button"
                data-testid="PointPanel-LocalModel_Button"
                type="default"
                href={Api.FWE_URL}
                target="_blank"
                rel="noopener"
                className={styles.localModelButton}
                icon={<PlusOutlined />}
              >
                Add local model
              </Button>
              {!shouldShowRemoveButton && (
                <Button
                  id="PointPanel-AddAllToCart_Button"
                  data-testid="PointPanel-AddAllToCart_Button"
                  type="primary"
                  loading={loadingCart}
                  onClick={() => handleAllToCart()}
                  disabled={!category}
                  className={styles.cartButton}
                >
                  Add Point to cart
                </Button>
              )}
              {shouldShowRemoveButton && (
                <Button
                  id="PointPanel-RemoveAllFromCart_Button"
                  data-testid="PointPanel-RemoveAllFromCart_Button"
                  type="primary"
                  danger
                  loading={loadingCart}
                  onClick={() => removeAllItemsFromPoint()}
                  className={styles.cartButton}
                >
                  Remove Point from cart
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
